import { GET_LIST_REQUEST_DEFAULT_LIMIT } from "constants/common";
import { useManageTable } from "hooks";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { EarningsParams, useEarnings } from "../apis";
import {
  EARNING_RESELLER_FILTER_SEARCH_PARAMETER,
  EARNING_SEARCH_SEARCH_PARAMETER,
  EARNING_TYPE_FILTER_SEARCH_PARAMETER,
  EarningChapters,
  EarningType
} from "../constants";

const useEarningsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // The three come from the URL so a commission row on a reseller profile can
  // link straight to the payout it produced.
  const resellerId = searchParams.get(
    EARNING_RESELLER_FILTER_SEARCH_PARAMETER
  );
  const type = searchParams.get(
    EARNING_TYPE_FILTER_SEARCH_PARAMETER
  ) as EarningType | null;

  const {
    page,
    searchValue,
    debouncedSearch,
    sorting,
    setSorting,
    setSearchValue,
    setPage
  } = useManageTable({
    defaultSearchValue: searchParams.get(EARNING_SEARCH_SEARCH_PARAMETER)
  });

  const { activeTab } = useParams();

  const setType = (nextType: EarningType | null) => {
    if (nextType) {
      searchParams.set(EARNING_TYPE_FILTER_SEARCH_PARAMETER, nextType);
    } else {
      searchParams.delete(EARNING_TYPE_FILTER_SEARCH_PARAMETER);
    }
    searchParams.delete("page");
    setSearchParams(searchParams);
  };

  const clearResellerFilter = () => {
    searchParams.delete(EARNING_RESELLER_FILTER_SEARCH_PARAMETER);
    searchParams.delete(EARNING_SEARCH_SEARCH_PARAMETER);
    searchParams.delete("page");
    setSearchParams(searchParams);
    setSearchValue("");
  };

  const requestParams: EarningsParams = useMemo(
    () => ({
      page,
      take: GET_LIST_REQUEST_DEFAULT_LIMIT,
      search: debouncedSearch,
      chapter: activeTab as EarningChapters,
      resellerId,
      type,
      orderBy: sorting[0]?.id ?? null,
      order: sorting[0] ? (sorting[0]?.desc ? "DESC" : "ASC") : "DESC"
    }),
    [page, debouncedSearch, sorting, activeTab, resellerId, type]
  );

  const { earnings, meta, isLoading, refetch } = useEarnings(requestParams);

  return {
    earnings,
    totalItems: meta?.itemCount ?? 0,
    searchValue,
    page,
    isLoading,
    sorting,
    setSorting,
    setSearchValue,
    setPage,
    type,
    setType,
    resellerId,
    clearResellerFilter,
    refetchEarnings: refetch
  };
};

export default useEarningsList;
